// 1st/2nd Half Goals — types + shaping (Phase 3).
//
// One team's goals scored/conceded split across the two halves. Two views:
//   - bars   — a 1H vs 2H split bar for Scored + one for Conceded (from the
//              per-game half averages).
//   - bands  — ten 5-min bands per half, goals-per-band (drawn on a <canvas>).
// Pure + framework-free; the D3 bar animation + the canvas draw live in the
// components. The two views read different datasets.

type Window = "5" | "10" | "Season";

// ---- Bars view ----

/** Per-game half averages (…Sc/…Co) + season totals (…T) for one window. */
export interface HalfGoalsWindow {
  h1Sc: number;
  h2Sc: number;
  h1Co: number;
  h2Co: number;
  h1ScT: number;
  h1CoT: number;
  h2ScT: number;
  h2CoT: number;
}

/** Half-goals (bars) data keyed by full team name, then window. */
export type HalfGoalsData = Record<string, Record<Window, HalfGoalsWindow>>;

/** A 1H/2H percentage split (rounded, summing ~100). */
export interface HalfSplit {
  p1: number;
  p2: number;
}

/** Shaped bars view: the two split rows. */
export interface HalfGoalsBarsView {
  scored: HalfSplit;
  conceded: HalfSplit;
}

function toWindow(timeline: string | null | undefined): Window {
  return timeline === "10" ? "10" : timeline === "Season" ? "Season" : "5";
}

/** Split two half-values into rounded percentages of their sum (legacy `pct`). */
function split(a: number, b: number): HalfSplit {
  const tot = a + b || 1;
  return { p1: Math.round((a / tot) * 100), p2: Math.round((b / tot) * 100) };
}

/**
 * Shape the bars view for a team + window. Returns null when missing (empty
 * state). Percentages are the 1H vs 2H share of the per-game half averages.
 */
export function shapeHalfGoalsBars(
  data: HalfGoalsData,
  team: string,
  timeline: string | null | undefined
): HalfGoalsBarsView | null {
  const td = data[team]?.[toWindow(timeline)];
  if (!td) return null;
  return {
    scored: split(td.h1Sc, td.h2Sc),
    conceded: split(td.h1Co, td.h2Co),
  };
}

// ---- Bands view ----

/** Goals per 5-min band, ten per half. */
export interface HalfGoalsBandsHalves {
  h1: number[];
  h2: number[];
}

/** One window's bands for scored + conceded. */
export interface HalfGoalsBandsWindow {
  scored: HalfGoalsBandsHalves;
  conceded: HalfGoalsBandsHalves;
}

/** Half-goals (bands) data keyed by full team name, then window. */
export type HalfGoalsBandsData = Record<
  string,
  Record<Window, HalfGoalsBandsWindow>
>;

/** The bands for a team + window (null when missing). */
export function selectHalfGoalsBands(
  data: HalfGoalsBandsData,
  team: string,
  timeline: string | null | undefined
): HalfGoalsBandsWindow | null {
  return data[team]?.[toWindow(timeline)] ?? null;
}

/** The 5-min band labels for each half (matching the legacy renderer). */
export const HALF_GOALS_H1_LABELS: readonly string[] = [
  "0–5'", "5–10'", "10–15'", "15–20'", "20–25'", "25–30'", "30–35'", "35–40'", "40–45'", "45+'",
];
export const HALF_GOALS_H2_LABELS: readonly string[] = [
  "45–50'", "50–55'", "55–60'", "60–65'", "65–70'", "70–75'", "75–80'", "80–85'", "85–90'", "90+'",
];
